import { inject, Injectable } from '@angular/core';
import { SuiClient } from '@mysten/sui/client';
import { environment } from '../../../environments/environment';
import { SettingHelper } from '../../helpers/setting.helper';

@Injectable({
    providedIn: 'root'
})
export class JsonrpcIndexer {

    /**
     * Setting helper
     */
    setting_helper = inject(SettingHelper);

    /**
     * Read object
     * 
     * @param id
     */
    async readObject(id: string): Promise<any> {
        // Init object
        let object: any = null;

        // Execute query
        let client = new SuiClient({ url: this.url() });
        let result = await client.getObject({
            id: id,
            options: {
                showContent: true
            }
        });

        // Process results
        if (result.data?.content?.dataType == 'moveObject') {
            object = this.flatten(result.data.content.fields);
        }

        return object;
    }

    /**
     * Read dynamic field
     * 
     * @param id
     * @param type
     * @param value
     */
    async readDynamicField(id: string, type: string, value: string): Promise<any> {
        // Init object
        let field: any = null;

        // Execute query
        let client = new SuiClient({ url: this.url() });
        let result = await client.getDynamicFieldObject({
            parentId: id,
            name: {
                type: type,
                value: value
            },
        });

        // Process results
        if (result.data?.content?.dataType == 'moveObject') {
            field = this.flatten(result.data?.content.fields).value;
        }

        return field;
    }

    /**
     * Read balance
     * 
     * @param id
     * @param signature
     */
    async readBalance(id: string, signature: string): Promise<bigint> {
        // Init balance
        let balance: bigint = BigInt(0);

        // Execute query
        let client = new SuiClient({ url: this.url() });
        let result = await client.getBalance({
            owner: id,
            coinType: signature
        });

        // Process results
        if (result.totalBalance) {
            balance = BigInt(result.totalBalance);
        }

        return balance;
    }

    /**
     * Read events
     * 
     * @param version
     * @param event_type
     * @param sender
     */
    async readEvents(version: number, event_type: string, sender?: string): Promise<Array<any>> {
        // Init events
        let events: Array<any> = [];

        // Execute query
        let result = null;
        let client = new SuiClient({ url: this.url() });
        if (sender) {
            result = await client.queryEvents({
                limit: environment.page_size * 10,
                order: 'descending',
                query: {
                    MoveEventType: environment.config.versions[version] + '::event::' + event_type
                }
            });
        } else {
            result = await client.queryEvents({
                limit: environment.page_size,
                order: 'descending',
                query: {
                    MoveEventType: environment.config.versions[version] + '::event::' + event_type
                }
            });
        }

        // Process results
        if (result?.data) {
            for (let i = 0; i < result.data.length; i++) {
                let timestamp = result.data[i].timestampMs;
                let digest = result.data[i].id.txDigest;
                let json = result.data[i].parsedJson;

                if (sender) {
                    if (result.data[i].sender == sender) {
                        events.push({
                            timestamp: timestamp,
                            transactionBlock: { digest: digest },
                            contents: { json: json }
                        });
                    }
                } else {
                    events.push({
                        timestamp: timestamp,
                        transactionBlock: { digest: digest },
                        contents: { json: json }
                    });
                }
            }
        }

        return events;
    }

    /**
     * Flatten object
     * 
     * @param object 
     */
    private flatten(object: any): any {
        if (Array.isArray(object)) {
            return object.map(item => this.flatten(item));
        } else if (typeof object === 'object' && object !== null) {
            const updated: any = {};
            for (const [key, value] of Object.entries(object)) {
                if (key === 'type') {
                    continue;
                } else if (key === 'fields' && typeof value === 'object' && value !== null) {
                    const fields = this.flatten(value);
                    Object.assign(updated, fields);
                } else {
                    updated[key] = this.flatten(value);
                }
            }

            return updated;
        } else {
            return object;
        }
    }

    /**
     * Find jsonrpc url
     */
    private url() {
        let url = environment.jsonrpc.default;

        let provider = this.setting_helper.jsonrpc();
        switch (provider) {
            case 'foundation':
            case 'suiscan':
            case 'publicnode':
            case 'blastapi':
                url = environment.jsonrpc[provider];
                break;
        }

        return url;
    }
}